import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { parse } from "date-fns";
import React from "react";
import { useForm } from "react-hook-form";

import { useToast } from "@/components/ui/use-toast";
import { useI18n } from "@/context";

import { reportsApi } from "./reports.api";
import { IUseReports } from "./reports.hooks.interface";
import { reportsFiltersSchema, TReportsFiltersFormData } from "./reports.schema";
import { 
  TSale, 
  TSaleDetailsResponse,
  TSaleListItem, 
  TSaleReportFilters, 
  TSaleStatus} from "./reports.types";

export const useReports = (): IUseReports => {
  const { t } = useI18n();
  const { toast } = useToast();
  
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [selectedSale, setSelectedSale] = React.useState<TSale | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = React.useState(false);
  const [filters, setFilters] = React.useState<TSaleReportFilters>({});
  
  const itemsPerPage = 20;

  const filtersForm = useForm<TReportsFiltersFormData>({
    resolver: zodResolver(reportsFiltersSchema),
    defaultValues: {
      status: "",
      partner_name: "",
      client_name: "",
      client_cpf: "",
      sale_id: "",
      start_date: "",
      end_date: "",
      minimum_price: "",
      maximum_price: "",
    },
  });

  // Query para buscar relatório de vendas
  const {
    data: salesReport,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["salesReport", filters, currentPage],
    queryFn: () => reportsApi.getSalesReport({
      ...filters,
      page: currentPage,
      limit: itemsPerPage,
    }),
    enabled: currentPage > 0 && itemsPerPage > 0, // Garante valores válidos
  });

  // Mutation para buscar detalhes da venda
  const getSaleDetailsMutation = useMutation({
    mutationFn: (transactionSearchId: string) =>
      reportsApi.getSaleDetails(transactionSearchId),
    onSuccess: (data) => {
      // Converte a resposta da API para o formato TSale
      const sale = mapSaleDetailsToSale(data);
      setSelectedSale(sale);
      setIsDetailsModalOpen(true);
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Função auxiliar para converter dados da API para TSale
  const mapSaleListItemToTSale = (item: TSaleListItem): TSale => {
    return {
      id: item.sale_id,
      plan_name: item.plan_name,
      partner_name: item.partner,
      client_name: item.customer_name,
      client_cpf: item.customer_cpf,
      sale_price: item.value_sale,
      status: mapStatusToTSaleStatus(item.status),
      sale_date: new Date(item.sale_date),
      client_details: {
        email: "",
        full_name: item.customer_name,
        cellphone: "",
        birthdate: "",
        cpf: item.customer_cpf,
        gender: "",
        zip: "",
        address: "",
        number: "",
        complement: "",
        district: "",
        city: "",
        state: "",
        external_customer_id: 0,
      },
      plan_details: {
        id: "",
        name: item.plan_name,
        description: item.plan_description,
        products: [],
      },
    };
  };

  // Função auxiliar para converter detalhes da venda para TSale
  const mapSaleDetailsToSale = (data: TSaleDetailsResponse): TSale => {
    return {
      id: data.sale_info.sale_id,
      plan_name: data.plan.plan_name,
      partner_name: data.sale_info.partner,
      client_name: data.customer.full_name,
      client_cpf: data.customer.cpf,
      sale_price: data.sale_info.total_amount,
      status: mapStatusToTSaleStatus(data.sale_info.status),
      sale_date: new Date(data.sale_info.sale_date),
      client_details: {
        email: data.customer.email,
        full_name: data.customer.full_name,
        cellphone: data.customer.cellphone,
        birthdate: data.customer.birthdate,
        cpf: data.customer.cpf,
        gender: data.customer.gender,
        zip: data.customer.address.cep,
        address: data.customer.address.street,
        number: data.customer.address.number,
        complement: data.customer.address.complement,
        district: data.customer.address.neighborhood,
        city: data.customer.address.city,
        state: data.customer.address.state,
        external_customer_id: 0,
      },
      plan_details: {
        id: data.plan.plan_id,
        name: data.plan.plan_name,
        description: data.plan.description,
        products: data.plan.products.map((p, index) => ({
          id: String(index),
          name: p.product_name,
          description: p.description,
          price: p.price,
        })),
      },
    };
  };

  // Função auxiliar para mapear status da API para TSaleStatus
  const mapStatusToTSaleStatus = (status: string): TSaleStatus => {
    // API retorna: "paid", "pending", "failed", "refunded"
    if (status === "paid" || status === "approved") return TSaleStatus.approved;
    if (status === "pending") return TSaleStatus.pending;
    if (status === "failed") return TSaleStatus.failed;
    if (status === "refunded") return TSaleStatus.refunded;
    // "rejected" e "cancelled" são mapeados para "rejected" e "cancelled" respectivamente
    // mas não aparecem como opção de filtro
    if (status === "rejected") return TSaleStatus.rejected;
    if (status === "cancelled") return TSaleStatus.cancelled;
    return TSaleStatus.pending;
  };

  const statusOptions = React.useMemo(
    () => [
      { value: TSaleStatus.pending, label: t("reports.status.pending") },
      { value: TSaleStatus.approved, label: t("reports.status.approved") },
      { value: TSaleStatus.failed, label: t("reports.status.failed") },
      { value: TSaleStatus.refunded, label: t("reports.status.refunded") },
    ],
    [t]
  );

  // Lista de parceiros únicos dos dados
  const partnerOptions = React.useMemo(() => {
    if (!salesReport?.data) return [];
    
    const uniquePartners = Array.from(
      new Set(salesReport.data.map((sale) => sale.partner))
    );
    return uniquePartners.map((partner) => ({
      value: partner,
      label: partner,
    }));
  }, [salesReport?.data]);

  // Função auxiliar para parsear data no formato dd/MM/yyyy
  const parseDate = React.useCallback((dateString: string): Date | null => {
    if (!dateString) return null;
    try {
      // Tenta parsear no formato dd/MM/yyyy
      const parsed = parse(dateString, "dd/MM/yyyy", new Date());
      return isNaN(parsed.getTime()) ? null : parsed;
    } catch {
      return null;
    }
  }, []);

  const handleApplyFilters = React.useCallback(
    (data: TReportsFiltersFormData) => {
      const newFilters: TSaleReportFilters = {};

      // Converte as datas para formato ISO
      const startDate = data.start_date ? parseDate(data.start_date) : null;
      const endDate = data.end_date ? parseDate(data.end_date) : null;

      if (startDate || endDate) {
        newFilters.sale_date = {};
        if (startDate) {
          newFilters.sale_date.start_date = startDate.toISOString().split("T")[0];
        }
        if (endDate) {
          newFilters.sale_date.end_date = endDate.toISOString().split("T")[0];
        }
      }

      if (data.status && data.status !== "") {
        // Mapeia os status do frontend para os status da API
        if (data.status === "approved") {
          newFilters.status = "paid";
        } else if (data.status === "pending") {
          newFilters.status = "pending";
        } else if (data.status === "failed") {
          newFilters.status = "failed";
        } else if (data.status === "refunded") {
          newFilters.status = "refunded";
        }
      }

      if (data.partner_name && data.partner_name !== "") {
        newFilters.partner = data.partner_name;
      }

      if (data.client_name && data.client_name !== "") {
        newFilters.customer_name = data.client_name;
      }

      if (data.client_cpf && data.client_cpf !== "") {
        newFilters.customer_cpf = data.client_cpf;
      }

      if (data.sale_id && data.sale_id !== "") {
        newFilters.sale_id = data.sale_id;
      }

      // Adiciona filtros de preço se fornecidos (converte de reais para centavos)
      if (data.minimum_price || data.maximum_price) {
        newFilters.value_sale = {};
        if (data.minimum_price && data.minimum_price !== "") {
          // Remove tudo exceto dígitos, vírgula e ponto
          const cleanedValue = data.minimum_price.replace(/[^\d,\.]/g, '');
          const minValue = parseFloat(cleanedValue.replace(',', '.'));
          if (!isNaN(minValue) && minValue > 0) {
            newFilters.value_sale.min = Math.round(minValue * 100); // Converte para centavos
          }
        }
        if (data.maximum_price && data.maximum_price !== "") {
          // Remove tudo exceto dígitos, vírgula e ponto
          const cleanedValue = data.maximum_price.replace(/[^\d,\.]/g, '');
          const maxValue = parseFloat(cleanedValue.replace(',', '.'));
          if (!isNaN(maxValue) && maxValue > 0) {
            newFilters.value_sale.max = Math.round(maxValue * 100); // Converte para centavos
          }
        }
      }

      setFilters(newFilters);
      setCurrentPage(1);
    },
    [parseDate]
  );

  const handleClearFilters = React.useCallback(() => {
    filtersForm.reset({
      status: "",
      partner_name: "",
      client_name: "",
      client_cpf: "",
      sale_id: "",
      start_date: "",
      end_date: "",
      minimum_price: "",
      maximum_price: "",
    });
    setFilters({});
    setCurrentPage(1);
  }, [filtersForm]);

  // Removido: filtro em tempo real - agora usa busca por API
  // Os filtros serão aplicados quando o usuário interagir com o formulário

  // Processa os dados retornados da API
  const filteredSales = React.useMemo(() => {
    if (!salesReport?.data) return [];
    return salesReport.data.map(mapSaleListItemToTSale);
  }, [salesReport?.data]);

  const paginatedSales = filteredSales;

  const handlePageChange = React.useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleOpenDetails = React.useCallback(async (sale: TSale) => {
    // Busca os detalhes completos da venda
    getSaleDetailsMutation.mutate(sale.id);
  }, [getSaleDetailsMutation]);

  const handleCloseDetails = React.useCallback(() => {
    setIsDetailsModalOpen(false);
    setSelectedSale(null);
  }, []);

  // Tratamento de erros
  React.useEffect(() => {
    if (error) {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    }
  }, [error, toast, t]);

  return {
    sales: filteredSales,
    filteredSales,
    paginatedSales,
    currentPage,
    totalPages: salesReport?.metadata?.total_pages || 1,
    totalItems: salesReport?.metadata?.total || 0,
    itemsPerPage,
    filtersForm,
    handleApplyFilters,
    handleClearFilters,
    handlePageChange,
    statusOptions,
    partnerOptions,
    selectedSale,
    isDetailsModalOpen,
    handleOpenDetails,
    handleCloseDetails,
    isLoading,
  };
};
